import { Info, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@kioskgaming/ui';
import type { DashboardKpi } from '../../services/api';

export type MetricStripItem = {
  id: string;
  label: string;
  tooltip: string;
  kpi?: DashboardKpi | null;
  value?: string;
  format?: 'currency' | 'number';
  invertDelta?: boolean;
  emphasis?: boolean;
  /** Tailwind class for the left colour bar (e.g. bg-emerald-100). */
  barClassName?: string;
  valueClassName?: string;
};

type Props = {
  items: MetricStripItem[];
  title?: string;
  columnsClassName?: string;
  className?: string;
};

function formatValue(value: number, format: MetricStripItem['format']): string {
  if (!Number.isFinite(value)) return '—';
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: Math.abs(value) >= 10000 ? 0 : 2,
    }).format(value);
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function Delta({ deltaPct, invert }: { deltaPct?: number | null; invert: boolean }) {
  if (deltaPct === undefined) return null;
  if (deltaPct === null) {
    return <span className="text-[10px] font-medium text-muted-foreground">N/A</span>;
  }
  const isFlat = Math.abs(deltaPct) < 0.05;
  const isImprovement = invert ? deltaPct < 0 : deltaPct > 0;
  const Icon = isFlat ? Minus : deltaPct > 0 ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-[10px] font-semibold',
        isFlat ? 'text-muted-foreground' : isImprovement ? 'text-emerald-600' : 'text-red-600',
      )}
    >
      <Icon className="h-2.5 w-2.5" />
      {deltaPct > 0 ? '+' : ''}
      {deltaPct.toLocaleString('en-US', { maximumFractionDigits: 1 })}%
    </span>
  );
}

/** Compact KPI strip matching Admin dashboard MetricStrip (one card, hairline cells). */
export default function MetricStrip({ items, title, columnsClassName, className }: Props) {
  const columns = columnsClassName || 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

  return (
    <div className={className}>
      {title ? (
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-blue-900/70">{title}</p>
      ) : null}
      <div className="overflow-hidden rounded-xl border border-blue-100 bg-white">
        <div className={cn('grid', columns)}>
          {items.map((item) => {
            const display =
              item.value ??
              (item.kpi != null ? formatValue(item.kpi.value, item.format ?? 'number') : '—');
            return (
              <div
                key={item.id}
                className="flex min-w-0 items-stretch gap-2 border-b border-r border-blue-100/70 px-3 py-2"
                title={item.tooltip}
              >
                <span
                  className={cn('w-0.5 shrink-0 rounded-full', item.barClassName || 'bg-blue-100')}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-start gap-1">
                    <span className="text-[10px] font-medium uppercase leading-tight tracking-wide text-muted-foreground">
                      {item.label}
                    </span>
                    <Info className="mt-px h-3 w-3 shrink-0 text-blue-300" aria-hidden />
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-1.5">
                    <span
                      className={cn(
                        'truncate font-semibold tabular-nums',
                        item.emphasis ? 'text-lg' : 'text-sm',
                        item.valueClassName || 'text-blue-950',
                      )}
                    >
                      {display}
                    </span>
                    <Delta deltaPct={item.kpi?.deltaPct} invert={item.invertDelta ?? false} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
